import { Navigate, Route, Routes } from 'react-router-dom'
import { useSession } from './api/session.js'
import { setLocale } from './i18n/index.js'
import { Spinner } from './components/ui.js'
import { BackofficeLayout } from './backoffice/Layout.js'
import { Dashboard } from './backoffice/Dashboard.js'
import { TasksPage } from './backoffice/Tasks.js'
import { IssuesPage } from './backoffice/Issues.js'
import { LoginPage } from './backoffice/Login.js'
import { PlansPage } from './backoffice/Plans.js'
import { CoveragePage } from './backoffice/Coverage.js'
import { LegionellaPage } from './backoffice/Legionella.js'
import { HandoverPage } from './backoffice/Handover.js'
import { QualityPage } from './backoffice/Quality.js'
import { SettingsPage } from './backoffice/Settings.js'
import { ObjectsPage } from './backoffice/Objects.js'
import { ReportsPage } from './backoffice/Reports.js'
import { ExportsPage } from './backoffice/Exports.js'
import { StaffDetailPage } from './backoffice/StaffDetail.js'
import { StaffPage } from './backoffice/Staff.js'
import { TemplatesPage } from './backoffice/Templates.js'
import { InvitePage } from './worker/Invite.js'
import { PlanPage } from './worker/Plan.js'
import { ReportIssuePage } from './worker/ReportIssue.js'
import { StatsPage } from './worker/Stats.js'
import { TaskDetailPage } from './worker/TaskDetail.js'
import { TodayPage } from './worker/Today.js'
import { TourPage } from './worker/Tour.js'
import { PortalAccountPage, PortalInvitePage, PortalLayout, PortalOverviewPage, PortalSitePage } from './portal/Portal.js'
import { PortalNewTicketPage, PortalTicketPage, PortalTicketsPage } from './portal/Tickets.js'

export function App() {
  const { data: session, isLoading } = useSession()

  if (isLoading) {
    return <div className="center-page"><Spinner /></div>
  }

  const role = session?.user.role
  const isWorker = role === 'minijobber'
  const isBackoffice = role === 'verwalter' || role === 'admin'
  const isCustomer = role === 'kunde'
  const home = isWorker ? '/app' : isCustomer ? '/portal' : isBackoffice ? '/' : '/login'
  // Set during render, before any child calls `t()`: the phone shows the
  // worker's language, the backoffice stays German.
  setLocale(isWorker ? (session?.user.locale ?? 'de') : 'de')

  return (
    <Routes>
      <Route path="/einladung/:token" element={<InvitePage />} />
      <Route path="/login" element={isBackoffice || isCustomer ? <Navigate to={home} replace /> : <LoginPage />} />
      <Route path="/portal/einladung/:token" element={<PortalInvitePage />} />
      <Route path="/zugang/:token" element={<PortalInvitePage />} />

      {/* Kundenportal */}
      <Route element={isCustomer ? <PortalLayout /> : <Navigate to={home} replace />}>
        <Route path="/portal" element={<PortalOverviewPage />} />
        <Route path="/portal/liegenschaft/:id" element={<PortalSitePage />} />
        <Route path="/portal/tickets" element={<PortalTicketsPage />} />
        <Route path="/portal/tickets/neu" element={<PortalNewTicketPage />} />
        <Route path="/portal/tickets/:id" element={<PortalTicketPage />} />
        <Route path="/portal/konto" element={<PortalAccountPage />} />
      </Route>

      {/* Minijobber app */}
      <Route path="/app" element={isWorker ? <TodayPage /> : <Navigate to="/login" replace />} />
      <Route path="/app/aufgabe/:id" element={isWorker ? <TaskDetailPage /> : <Navigate to="/login" replace />} />
      <Route path="/app/plan" element={isWorker ? <PlanPage /> : <Navigate to="/login" replace />} />
      <Route path="/app/melden" element={isWorker ? <ReportIssuePage /> : <Navigate to="/login" replace />} />
      <Route path="/app/zahlen" element={isWorker ? <StatsPage /> : <Navigate to="/login" replace />} />
      <Route path="/app/tour" element={isWorker ? <TourPage /> : <Navigate to="/login" replace />} />

      {/* Backoffice */}
      <Route element={isBackoffice ? <BackofficeLayout /> : <Navigate to={home} replace />}>
        <Route path="/" element={<Navigate to="/aufgaben" replace />} />
        <Route path="/uebersicht" element={<Dashboard />} />
        <Route path="/aufgaben" element={<TasksPage />} />
        <Route path="/kalender" element={<Navigate to="/aufgaben" replace />} />
        <Route path="/plaene" element={<PlansPage />} />
        <Route path="/legionellen" element={<LegionellaPage />} />
        <Route path="/objekte" element={<ObjectsPage />} />
        <Route path="/objekte/:id" element={<ObjectsPage />} />
        <Route path="/liegenschaften" element={<Navigate to="/objekte" replace />} />
        <Route path="/uebergabe" element={<HandoverPage />} />
        <Route path="/mitarbeiter" element={<StaffPage />} />
        <Route path="/mitarbeiter/:id" element={<StaffDetailPage />} />
        <Route path="/mitarbeiter/:id/vertretung" element={<CoveragePage />} />
        <Route path="/qualitaet" element={<QualityPage />} />
        <Route path="/einstellungen" element={<SettingsPage />} />
        <Route path="/vorlagen" element={<TemplatesPage />} />
        <Route path="/maengel" element={<IssuesPage />} />
        <Route path="/nachweise" element={<ReportsPage />} />
        <Route path="/auswertungen" element={<ExportsPage />} />
      </Route>

      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
  )
}
